import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Button
} from '@mui/material';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export const INDIVIDUAL_DATA_OPTIONS = {
  ocUse: ['Unknown', 'Never Used', 'Former Use', 'Current Use'],
  mhtUse: ['Unknown', 'Never Used', 'Former Use', 'Current Use'],
  menopause: [
    'Unknown',
    'Pre-menopausal',
    'Peri-menopausal',
    'Post-menopausal'
  ],
  breastFeeding: ['Unknown', 'Yes', 'No'],
  biopsy: ['Unknown', 'No', 'Yes']
};

export const INDIVIDUAL_DATA_FIELDS = {
  left: [
    { key: 'menarche', label: 'Menarche (Age in Years)', type: 'number' },
    {
      key: 'parity',
      label: 'Parity (No. pregnancies viable gestation)',
      type: 'number'
    },
    {
      key: 'ageFirstLiveBirth',
      label: 'Age at First Live Birth',
      type: 'number'
    },
    { key: 'bmi', label: 'BMI', type: 'number' },
    {
      key: 'alcoholGramsPerDay',
      label: 'Daily Alcohol Intake (grams per day)',
      type: 'number'
    },
    { key: 'birads', label: 'BI-RADS (mammographic density)', type: 'number' },
    { key: 'height', label: 'Height (cm)', type: 'number' },
    { key: 'weight', label: 'Weight (kg)', type: 'number' },
    { key: 'tubalLigation', label: 'Tubal Ligation procedure', type: 'yesno' },
    { key: 'endometriosis', label: 'Endometriosis', type: 'yesno' }
  ],
  right: [
    {
      key: 'ocUse',
      label: 'OC Use',
      type: 'select',
      options: 'ocUse',
      auxKey: 'years',
      auxLabel: 'Number of years'
    },
    {
      key: 'mhtUse',
      label: 'MHT Use',
      type: 'select',
      options: 'mhtUse',
      auxKey: 'years',
      auxLabel: 'Number of years'
    },
    {
      key: 'menopause',
      label: 'Menopause Status',
      type: 'select',
      options: 'menopause',
      auxKey: 'age',
      auxLabel: 'Age at menopause'
    },
    {
      key: 'breastFeeding',
      label: 'Performed Breast Feeding?',
      type: 'select',
      options: 'breastFeeding',
      auxKey: 'months',
      auxLabel: 'No. months'
    },
    { key: 'smoker', label: 'Smoker Status', type: 'yesno' },
    {
      key: 'biopsy',
      label: 'Biopsy History',
      type: 'select',
      options: 'biopsy',
      auxKey: 'count',
      auxLabel: 'No. biopsies'
    },
    {
      key: 'mammographicDensityVolpara',
      label: 'Mammographic Density %Volpara',
      type: 'number'
    },
    {
      key: 'mammographicDensityVAS',
      label: 'Mammographic Density %VAS',
      type: 'number'
    }
  ],
  prs: [
    {
      key: 'prsBreast',
      label: 'Polygenic Risk Score (Breast Cancer)',
      type: 'prs'
    },
    {
      key: 'prsOvarian',
      label: 'Polygenic Risk Score (Ovarian Cancer)',
      type: 'prs'
    },
    {
      key: 'prsProstate',
      label: 'Polygenic Risk Score (Prostate Cancer)',
      type: 'prs'
    }
  ]
};

export default function IndividualDataForm({
  value,
  onChange,
  disabled = false,
  prsSuggestions = {}
}) {
  const t = useAutoTranslation();

  const indiv = value || {};
  const updateIndiv = (patch) => onChange?.({ ...indiv, ...patch });

  const labelledRow = (key, label, control, labelFlex = '0 0 46%') => (
    <Box
      key={key}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 1.75,
        minWidth: 0
      }}
    >
      <Box sx={{ flex: labelFlex, minWidth: 0 }}>
        <FormLabel>{label}</FormLabel>
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        {control}
      </Box>
    </Box>
  );

  // numeric value + "Unknown" checkbox → stored as { unknown, value }
  const renderNumberUnknown = (key, label) => {
    const f = indiv[key] || {};
    return labelledRow(
      key,
      label,
      <>
        <TextField
          size="small"
          type="number"
          sx={{ maxWidth: 130 }}
          disabled={disabled || Boolean(f.unknown)}
          value={f.unknown ? '' : f.value ?? ''}
          onChange={(e) =>
            updateIndiv({ [key]: { ...f, value: e.target.value } })
          }
        />
        <FormControlLabel
          sx={{ m: 0, whiteSpace: 'nowrap' }}
          control={
            <Checkbox
              size="small"
              disabled={disabled}
              checked={Boolean(f.unknown)}
              onChange={(e) =>
                updateIndiv({ [key]: { ...f, unknown: e.target.checked } })
              }
            />
          }
          label={t('Unknown')}
        />
      </>
    );
  };

  // dropdown status (+ optional numeric aux) → stored as { status, [auxKey] }
  const renderSelectAux = (key, label, options, auxKey, auxLabel) => {
    const f = indiv[key] || {};
    const status = f.status ?? 'Unknown';
    const showAux =
      Boolean(auxKey) && status !== 'Unknown' && !/never/i.test(status);
    return labelledRow(
      key,
      label,
      // dropdown + aux kept in their own non-wrapping row: the dropdown shrinks
      // and the aux stays beside it instead of dropping to the next line.
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: 1
        }}
      >
        <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
          <Select
            value={status}
            disabled={disabled}
            onChange={(e) =>
              updateIndiv({ [key]: { ...f, status: e.target.value } })
            }
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {showAux && (
          <TextField
            size="small"
            type="number"
            placeholder={auxLabel}
            sx={{ width: 130, flexShrink: 0 }}
            disabled={disabled}
            value={f[auxKey] ?? ''}
            onChange={(e) =>
              updateIndiv({ [key]: { ...f, [auxKey]: e.target.value } })
            }
          />
        )}
      </Box>
    );
  };

  // Unknown / Yes / No radio → stored as a string
  const renderYesNoUnknown = (key, label) => {
    const val = indiv[key] ?? 'Unknown';
    return labelledRow(
      key,
      label,
      <RadioGroup
        row
        value={val}
        onChange={(e) => updateIndiv({ [key]: e.target.value })}
      >
        {['Unknown', 'Yes', 'No'].map((o) => (
          <FormControlLabel
            key={o}
            value={o}
            control={<Radio size="small" disabled={disabled} />}
            label={o}
          />
        ))}
      </RadioGroup>
    );
  };

  // PRS row: "Unknown" checkbox + alpha + zscore → { unknown, alpha, zscore }
  const renderPrs = (key, label) => {
    const f = indiv[key] || { unknown: true };
    const fieldsDisabled = disabled || Boolean(f.unknown);

    // A matching-trait peer 'prs' record (from the PRS tab) offers its
    // z-score here as a one-click suggestion — never auto-applied. alpha is
    // only ever included when the record's pgsId matches a specific panel
    // CanRisk has published a verified alpha for (verifiedAlpha, resolved by
    // RiskFactorForm) — never guessed from the trait match alone.
    const suggestion = prsSuggestions?.[key];
    const suggestedZscore = suggestion?.prsResult?.zscore;
    const verifiedAlpha = suggestion?.verifiedAlpha;
    const showSuggestion =
      suggestedZscore != null &&
      (String(f.zscore ?? '') !== String(suggestedZscore) ||
        (verifiedAlpha &&
          String(f.alpha ?? '') !== String(verifiedAlpha.alpha)));

    return labelledRow(
      key,
      label,
      <>
        <FormControlLabel
          sx={{ m: 0, whiteSpace: 'nowrap' }}
          control={
            <Checkbox
              size="small"
              disabled={disabled}
              checked={Boolean(f.unknown)}
              onChange={(e) =>
                updateIndiv({ [key]: { ...f, unknown: e.target.checked } })
              }
            />
          }
          label={t('Unknown')}
        />
        <TextField
          size="small"
          type="number"
          placeholder={t('alpha')}
          sx={{ width: 90 }}
          disabled={fieldsDisabled}
          value={fieldsDisabled ? '' : f.alpha ?? ''}
          onChange={(e) =>
            updateIndiv({ [key]: { ...f, alpha: e.target.value } })
          }
        />
        <TextField
          size="small"
          type="number"
          placeholder={t('zscore')}
          sx={{ width: 90 }}
          disabled={fieldsDisabled}
          value={fieldsDisabled ? '' : f.zscore ?? ''}
          onChange={(e) =>
            updateIndiv({ [key]: { ...f, zscore: e.target.value } })
          }
        />
        {showSuggestion && (
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            onClick={() =>
              updateIndiv({
                [key]: {
                  ...f,
                  unknown: false,
                  zscore: String(suggestedZscore),
                  ...(verifiedAlpha
                    ? { alpha: String(verifiedAlpha.alpha) }
                    : {})
                }
              })
            }
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0 }}
          >
            {verifiedAlpha
              ? t(
                  'Use saved PRS: alpha={{alpha}}, z={{suggestedZscore}} ({{canriskPanelName}})',
                  {
                    alpha: verifiedAlpha.alpha,
                    suggestedZscore,
                    canriskPanelName: verifiedAlpha.canriskPanelName
                  }
                )
              : t('Use saved PRS z={{suggestedZscore}}{{value}}', {
                  suggestedZscore,
                  value: suggestion?.prsResult?.pgsName
                    ? ` (${suggestion.prsResult.pgsName})`
                    : ''
                })}
          </Button>
        )}
      </>,
      '0 0 38%'
    );
  };

  // Render a field row from its config entry (drives off INDIVIDUAL_DATA_FIELDS).
  const renderField = (field) => {
    switch (field.type) {
      case 'number':
        return renderNumberUnknown(field.key, field.label);
      case 'select':
        return renderSelectAux(
          field.key,
          field.label,
          INDIVIDUAL_DATA_OPTIONS[field.options],
          field.auxKey,
          field.auxLabel
        );
      case 'yesno':
        return renderYesNoUnknown(field.key, field.label);
      case 'prs':
        return renderPrs(field.key, field.label);
      default:
        return null;
    }
  };

  return (
    <Box>
      <Grid container columnSpacing={5}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}
        >
          {INDIVIDUAL_DATA_FIELDS.left.map(renderField)}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}
        >
          {INDIVIDUAL_DATA_FIELDS.right.map(renderField)}
        </Grid>
      </Grid>
      {INDIVIDUAL_DATA_FIELDS.prs.map(renderField)}
    </Box>
  );
}
